// People & Crews management (Phase 2.5). PEOPLE-FIRST: the flat field-crew
// list is the primary surface; each person carries a multi-select chip strip
// of the preset crews they belong to (crew_memberships, many-to-many). Bot
// admins (crew_members.role='admin' — the office-manager Telegram identity,
// NOT field crew, NOT on the calendar) live in their own subsection. "Crew
// presets" is a SECONDARY, collapsed section for managing the preset crews
// themselves.
//
// NOTE: every `role`/admin reference here is `crew_members.role`. It is
// UNRELATED to `app_users.role='admin'` (the web super-user) — two separate
// identity tables that happen to share the value 'admin'.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check as CheckIcon,
  ChevronDown,
  Pencil,
  Plus,
  Trash2,
  Users,
  X as XIcon,
} from 'lucide-react';
import {
  deleteCrew,
  deleteCrewMember,
  getCrewMembers,
  getCrews,
  patchCrew,
  patchCrewMember,
  postCrew,
  postCrewMember,
  type CrewMemberRole,
  type CrewMemberRow,
} from '../api/client.js';
import type { CrewRow, CrewCreateInput } from '@gutterinv/shared';
import { Card } from './ui/card.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { Skeleton } from './ui/skeleton.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog.js';
import { cn } from '../lib/utils.js';

// Field-crew roles only; admins are managed in the Bot admins subsection.
const FIELD_ROLE_OPTIONS: { value: CrewMemberRole; label: string }[] = [
  { value: 'crew', label: 'Crew' },
  { value: 'admin', label: 'Bot admin' },
];

export function PeopleManagementSection() {
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [addBotAdminOpen, setAddBotAdminOpen] = useState(false);
  const [addPresetOpen, setAddPresetOpen] = useState(false);
  const [presetsOpen, setPresetsOpen] = useState(false);

  const { data: crews, isLoading: crewsLoading } = useQuery({
    queryKey: ['crews'],
    queryFn: getCrews,
  });
  const { data: members, isLoading: membersLoading } = useQuery({
    queryKey: ['crew-members'],
    queryFn: getCrewMembers,
  });

  const sortedCrews = useMemo(
    () => [...(crews ?? [])].sort((a, b) => a.displayOrder - b.displayOrder),
    [crews],
  );
  const activeCrews = useMemo(() => sortedCrews.filter((c) => c.active), [sortedCrews]);

  // Partition by role: field crew vs bot admins.
  const fieldPeople = useMemo(
    () =>
      (members ?? [])
        .filter((m) => m.role !== 'admin')
        .sort((a, b) => a.name.localeCompare(b.name)),
    [members],
  );
  const botAdmins = useMemo(
    () =>
      (members ?? [])
        .filter((m) => m.role === 'admin')
        .sort((a, b) => a.name.localeCompare(b.name)),
    [members],
  );

  // How many people sit in each preset — drives the delete confirm.
  const memberCountByCrew = useMemo(() => {
    const map = new Map<string, number>();
    for (const m of members ?? []) {
      for (const c of m.crews) map.set(c.id, (map.get(c.id) ?? 0) + 1);
    }
    return map;
  }, [members]);

  const isLoading = crewsLoading || membersLoading;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={() => setAddBotAdminOpen(true)}>
          <Plus className="h-4 w-4" />
          Add bot admin
        </Button>
        <Button size="sm" onClick={() => setAddPersonOpen(true)}>
          <Plus className="h-4 w-4" />
          Add person
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : (
        <div className="space-y-2">
          {/* PRIMARY: the flat field-crew list. */}
          <PeopleListCard people={fieldPeople} activeCrews={activeCrews} />

          {/* Bot admins — only shown when present. */}
          {botAdmins.length > 0 && <BotAdminsCard members={botAdmins} />}

          {/* SECONDARY: preset-crew management, collapsed by default. */}
          <CrewPresetsSection
            crews={sortedCrews}
            memberCountByCrew={memberCountByCrew}
            open={presetsOpen}
            onToggle={() => setPresetsOpen((v) => !v)}
            onAdd={() => setAddPresetOpen(true)}
          />
        </div>
      )}

      <p className="text-xs text-taupe">
        People are scheduled individually — assign anyone to a job on the
        calendar. Crew presets are just shortcuts that add a group of people at
        once. Bot admins see every crew&apos;s jobs on /schedule and can confirm
        pulls / installs on any crew&apos;s behalf, but are never assigned to
        jobs themselves.
      </p>

      <AddPersonDialog
        open={addPersonOpen}
        onOpenChange={setAddPersonOpen}
        activeCrews={activeCrews}
      />
      <AddBotAdminDialog open={addBotAdminOpen} onOpenChange={setAddBotAdminOpen} />
      <AddPresetDialog open={addPresetOpen} onOpenChange={setAddPresetOpen} />
    </div>
  );
}

// ============================================================
// EDITABLE NAME — click pencil to edit, Enter saves, Esc cancels.
// ============================================================

function EditableName({
  value,
  onSave,
  disabled,
}: {
  value: string;
  onSave: (next: string) => void;
  disabled?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [editing, value]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) onSave(trimmed);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-1">
        <span className="text-sm font-semibold text-foreground">{value}</span>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0 hover:text-primary"
          onClick={() => setEditing(true)}
          disabled={disabled}
          title="Rename"
        >
          <Pencil className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <Input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') {
            e.stopPropagation();
            setEditing(false);
          }
        }}
        disabled={disabled}
        autoFocus
        className="h-7 max-w-[12rem] text-sm"
      />
      <Button
        size="sm"
        variant="ghost"
        className="h-6 w-6 p-0 hover:text-primary"
        onClick={commit}
        disabled={disabled || !draft.trim()}
        title="Save"
      >
        <CheckIcon className="h-3 w-3" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="h-6 w-6 p-0"
        onClick={() => setEditing(false)}
        disabled={disabled}
        title="Cancel"
      >
        <XIcon className="h-3 w-3" />
      </Button>
    </div>
  );
}

// ============================================================
// ACTIVE TOGGLE — small pill switch shared by person/admin rows.
// ============================================================

function ActiveToggle({
  active,
  disabled,
  onToggle,
  title,
}: {
  active: boolean;
  disabled?: boolean;
  onToggle: () => void;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      title={title}
      className={cn(
        'inline-flex h-5 w-9 items-center rounded-full transition-colors',
        active ? 'bg-primary' : 'bg-muted',
      )}
    >
      <span
        className={cn(
          'inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform',
          active ? 'translate-x-[18px]' : 'translate-x-[3px]',
        )}
      />
    </button>
  );
}

// ============================================================
// PRESET CHIP EDITOR — per-person multi-select of preset crews.
// Each add/remove sends the FULL crewIds set (the API replaces all
// memberships). Optimistic cache write for instant chips.
// ============================================================

function PresetChipEditor({
  member,
  activeCrews,
}: {
  member: CrewMemberRow;
  activeCrews: CrewRow[];
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: (crewIds: string[]) => patchCrewMember(member.id, { crewIds }),
    onMutate: async (crewIds) => {
      await queryClient.cancelQueries({ queryKey: ['crew-members'] });
      const prev = queryClient.getQueryData<CrewMemberRow[]>(['crew-members']);
      // Resolve name/color from the union of current chips + active crews.
      const lookup = new Map<string, { id: string; name: string; color: string }>();
      for (const c of member.crews) lookup.set(c.id, c);
      for (const c of activeCrews) lookup.set(c.id, { id: c.id, name: c.name, color: c.color });
      const nextCrews = crewIds
        .map((id) => lookup.get(id))
        .filter((c): c is { id: string; name: string; color: string } => !!c);
      const rep = nextCrews[0] ?? null;
      queryClient.setQueryData<CrewMemberRow[]>(['crew-members'], (old) =>
        (old ?? []).map((m) =>
          m.id === member.id
            ? {
                ...m,
                crews: nextCrews,
                crewId: rep?.id ?? null,
                crewName: rep?.name ?? null,
                crewColor: rep?.color ?? null,
              }
            : m,
        ),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['crew-members'], ctx.prev);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['crew-members'] });
    },
  });

  const current = member.crews;
  const currentIds = new Set(current.map((c) => c.id));
  const addable = activeCrews.filter((c) => !currentIds.has(c.id));

  const addCrew = (id: string) => {
    mutation.mutate([...current.map((c) => c.id), id]);
    setOpen(false);
  };
  const removeCrew = (id: string) => {
    mutation.mutate(current.map((c) => c.id).filter((x) => x !== id));
  };

  return (
    <div className="flex flex-wrap items-center gap-1">
      {current.length === 0 && (
        <span className="text-xs text-taupe">No presets</span>
      )}
      {current.map((c) => (
        <span
          key={c.id}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-secondary/20 px-1.5 py-0.5 text-xs"
        >
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: c.color }}
          />
          {c.name}
          <button
            type="button"
            aria-label={`Remove ${c.name}`}
            onClick={() => removeCrew(c.id)}
            disabled={mutation.isPending}
            className="rounded-sm hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
          >
            <XIcon className="h-3 w-3" />
          </button>
        </span>
      ))}

      {addable.length > 0 && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && open) {
                e.stopPropagation();
                setOpen(false);
              }
            }}
            onBlur={() => {
              // Delay so an onMouseDown on a list row registers first.
              window.setTimeout(() => setOpen(false), 120);
            }}
            disabled={mutation.isPending}
            className="rounded-md border border-dashed border-border px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-accent/40 disabled:opacity-50"
          >
            <Plus className="mr-0.5 inline h-3 w-3" />
            preset
          </button>
          {open && (
            <ul className="absolute z-20 mt-1 max-h-48 w-44 overflow-auto rounded-md border border-border bg-card py-1 shadow-md">
              {addable.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      addCrew(c.id);
                    }}
                    className="flex w-full items-center gap-2 px-2 py-1 text-left text-sm hover:bg-accent/40"
                  >
                    <span
                      className="inline-block h-2 w-2 rounded-full"
                      style={{ backgroundColor: c.color }}
                    />
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// PEOPLE LIST — primary surface. One row per field-crew member.
// ============================================================

function PeopleListCard({
  people,
  activeCrews,
}: {
  people: CrewMemberRow[];
  activeCrews: CrewRow[];
}) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 bg-secondary/30 px-3 py-2">
        <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="text-sm font-semibold text-foreground">People</span>
        <span className="text-xs text-taupe">
          {people.length} crew member{people.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="overflow-x-auto border-t border-border">
        {/* table-fixed + shared colgroup so the Bot Admins table (below) can
            align its columns to these. Percent widths sum to 100. */}
        <table className="w-full table-fixed">
          <colgroup>
            <col style={{ width: '24%' }} />{/* Name */}
            <col style={{ width: '15%' }} />{/* Role */}
            <col style={{ width: '17%' }} />{/* Telegram ID */}
            <col style={{ width: '24%' }} />{/* Crew presets */}
            <col style={{ width: '10%' }} />{/* Active */}
            <col style={{ width: '10%' }} />{/* Actions */}
          </colgroup>
          <thead className="bg-secondary/10">
            <tr className="border-b border-border">
              <th className="px-3 py-2 text-left"><span className="eyebrow">Name</span></th>
              <th className="px-3 py-2 text-left"><span className="eyebrow">Role</span></th>
              <th className="px-3 py-2 text-left"><span className="eyebrow">Telegram ID</span></th>
              <th className="px-3 py-2 text-left"><span className="eyebrow">Crew presets</span></th>
              <th className="px-3 py-2 text-center"><span className="eyebrow">Active</span></th>
              <th className="px-3 py-2 text-right"><span className="eyebrow">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {people.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-center text-xs text-taupe">
                  No crew members yet. Add a person above.
                </td>
              </tr>
            ) : (
              people.map((m) => (
                <PersonRow key={m.id} member={m} activeCrews={activeCrews} />
              ))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function PersonRow({
  member,
  activeCrews,
}: {
  member: CrewMemberRow;
  activeCrews: CrewRow[];
}) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (input: {
      name?: string;
      active?: boolean;
      role?: CrewMemberRole;
      crewIds?: string[];
    }) => patchCrewMember(member.id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['crew-members'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteCrewMember(member.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['crew-members'] });
    },
  });

  const handleDelete = () => {
    const ok = window.confirm(
      `Remove ${member.name}?\n\nIf they've authored any work (estimates, jobs, inventory txns), the delete will be blocked — toggle Active off instead.`,
    );
    if (!ok) return;
    deleteMutation.mutate();
  };

  return (
    <tr
      className={cn(
        'border-b border-border last:border-b-0 hover:bg-primary/[0.04]',
        !member.active && 'opacity-60',
      )}
    >
      <td className="px-3 py-2">
        <EditableName
          value={member.name}
          onSave={(name) => mutation.mutate({ name })}
          disabled={mutation.isPending}
        />
      </td>
      <td className="px-3 py-2">
        <select
          value={member.role}
          onChange={(e) => {
            const newRole = e.target.value as CrewMemberRole;
            // Promote to bot admin: clear presets + move to the Bot admins
            // subsection. Bot admins are never assigned to jobs.
            mutation.mutate(
              newRole === 'admin' ? { role: newRole, crewIds: [] } : { role: newRole },
            );
          }}
          disabled={mutation.isPending}
          className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {FIELD_ROLE_OPTIONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2">
        <span className="font-mono text-xs text-taupe">{member.telegramUserId}</span>
      </td>
      <td className="min-w-0 px-3 py-2">
        <PresetChipEditor member={member} activeCrews={activeCrews} />
      </td>
      <td className="px-3 py-2 text-center">
        <ActiveToggle
          active={member.active}
          disabled={mutation.isPending}
          onToggle={() => mutation.mutate({ active: !member.active })}
        />
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-col items-end gap-0.5">
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="h-7 w-7 p-0 hover:text-destructive"
            title="Remove member"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
          {(mutation.error || deleteMutation.error) && (
            <span className="whitespace-nowrap text-[0.625rem] text-destructive">
              {(mutation.error ?? deleteMutation.error) instanceof Error
                ? ((mutation.error ?? deleteMutation.error) as Error).message
                : 'Failed'}
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

// ============================================================
// BOT ADMINS — crew_members.role='admin'. Telegram identities with
// tenant-wide bot visibility; NOT field crew, NO presets, NOT assignable.
// ============================================================

function BotAdminsCard({ members }: { members: CrewMemberRow[] }) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 bg-secondary/30 px-3 py-2">
        <Users className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="text-sm font-semibold text-foreground">Bot admins</span>
        <span className="text-xs text-taupe">
          {members.length} member{members.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="overflow-x-auto border-t border-border">
        {/* Same table-fixed + colgroup as the People table so columns align.
            Admins have no crew presets, so that column is an empty spacer. */}
        <table className="w-full table-fixed">
          <colgroup>
            <col style={{ width: '24%' }} />{/* Name */}
            <col style={{ width: '15%' }} />{/* Role */}
            <col style={{ width: '17%' }} />{/* Telegram ID */}
            <col style={{ width: '24%' }} />{/* Crew presets (empty for admins) */}
            <col style={{ width: '10%' }} />{/* Active */}
            <col style={{ width: '10%' }} />{/* Actions */}
          </colgroup>
          <thead className="bg-secondary/10">
            <tr className="border-b border-border">
              <th className="px-3 py-2 text-left"><span className="eyebrow">Name</span></th>
              <th className="px-3 py-2 text-left"><span className="eyebrow">Role</span></th>
              <th className="px-3 py-2 text-left"><span className="eyebrow">Telegram ID</span></th>
              <th className="px-3 py-2" aria-hidden="true" />
              <th className="px-3 py-2 text-center"><span className="eyebrow">Active</span></th>
              <th className="px-3 py-2 text-right"><span className="eyebrow">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <BotAdminRow key={m.id} member={m} />
            ))}
          </tbody>
        </table>
      </div>
      <p className="border-t border-border px-3 py-2 text-xs text-taupe">
        Bot admins see every crew&apos;s jobs on /schedule and can confirm pulls
        / installs on any crew&apos;s behalf. They are not field crew — change
        their role to Crew to make them assignable to jobs.
      </p>
    </Card>
  );
}

function BotAdminRow({ member }: { member: CrewMemberRow }) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (input: { name?: string; active?: boolean; role?: CrewMemberRole }) =>
      patchCrewMember(member.id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['crew-members'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteCrewMember(member.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['crew-members'] });
    },
  });

  const handleDelete = () => {
    const ok = window.confirm(
      `Remove bot admin ${member.name}?\n\nIf they've authored any work, the delete will be blocked — toggle Active off instead.`,
    );
    if (!ok) return;
    deleteMutation.mutate();
  };

  return (
    <tr
      className={cn(
        'border-b border-border last:border-b-0 hover:bg-primary/[0.04]',
        !member.active && 'opacity-60',
      )}
    >
      <td className="px-3 py-2">
        <EditableName
          value={member.name}
          onSave={(name) => mutation.mutate({ name })}
          disabled={mutation.isPending}
        />
      </td>
      <td className="px-3 py-2">
        <select
          value={member.role}
          onChange={(e) => {
            // Demote to a field role → drops into the People list with an
            // empty preset strip the office can fill. No crewIds sent (they
            // had none as an admin).
            mutation.mutate({ role: e.target.value as CrewMemberRole });
          }}
          disabled={mutation.isPending}
          className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {FIELD_ROLE_OPTIONS.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2">
        <span className="font-mono text-xs text-taupe">{member.telegramUserId}</span>
      </td>
      {/* Crew presets — empty for admins (aligns with the People table). */}
      <td className="px-3 py-2" aria-hidden="true" />
      <td className="px-3 py-2 text-center">
        <ActiveToggle
          active={member.active}
          disabled={mutation.isPending}
          onToggle={() => mutation.mutate({ active: !member.active })}
        />
      </td>
      <td className="px-3 py-2">
        <div className="flex flex-col items-end gap-0.5">
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="h-7 w-7 p-0 hover:text-destructive"
            title="Remove bot admin"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
          {(mutation.error || deleteMutation.error) && (
            <span className="whitespace-nowrap text-[0.625rem] text-destructive">
              {(mutation.error ?? deleteMutation.error) instanceof Error
                ? ((mutation.error ?? deleteMutation.error) as Error).message
                : 'Failed'}
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

// ============================================================
// CREW PRESETS — secondary, collapsible. Manage the preset crews
// themselves (rename / recolor / active / delete). Membership is edited
// per-person in the People list above.
// ============================================================

function CrewPresetsSection({
  crews,
  memberCountByCrew,
  open,
  onToggle,
  onAdd,
}: {
  crews: CrewRow[];
  memberCountByCrew: Map<string, number>;
  open: boolean;
  onToggle: () => void;
  onAdd: () => void;
}) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 bg-secondary/20 px-3 py-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex flex-1 items-center gap-2 text-left"
          aria-expanded={open}
        >
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
              open ? '' : '-rotate-90',
            )}
          />
          <span className="text-sm font-medium text-muted-foreground">Crew presets</span>
          <span className="text-xs text-taupe">
            {crews.length} preset{crews.length === 1 ? '' : 's'}
          </span>
        </button>
        <Button size="sm" variant="ghost" className="text-xs" onClick={onAdd}>
          <Plus className="h-3.5 w-3.5" />
          Add crew preset
        </Button>
      </div>

      {open && (
        <div className="border-t border-border">
          {crews.length === 0 ? (
            <p className="px-3 py-4 text-center text-xs text-taupe">
              No crew presets yet. Presets let you add a group of people to a
              job in one click.
            </p>
          ) : (
            crews.map((crew) => (
              <PresetCard
                key={crew.id}
                crew={crew}
                memberCount={memberCountByCrew.get(crew.id) ?? 0}
              />
            ))
          )}
        </div>
      )}
    </Card>
  );
}

function PresetCard({ crew, memberCount }: { crew: CrewRow; memberCount: number }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(crew.name);
  const [color, setColor] = useState(crew.color);

  useEffect(() => {
    if (!editing) {
      setName(crew.name);
      setColor(crew.color);
    }
  }, [editing, crew.name, crew.color]);

  const updateMutation = useMutation({
    mutationFn: () => patchCrew(crew.id, { name: name.trim(), color }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['crews'] });
      setEditing(false);
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (next: boolean) => patchCrew(crew.id, { active: next }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['crews'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteCrew(crew.id),
    onSuccess: () => {
      // Deleting a preset cascades its crew_memberships; refresh people too
      // so their chip strips drop the dead preset.
      void queryClient.invalidateQueries({ queryKey: ['crews'] });
      void queryClient.invalidateQueries({ queryKey: ['crew-members'] });
    },
  });

  const handleDelete = () => {
    const suffix =
      memberCount > 0
        ? `\n\n${memberCount} ${memberCount === 1 ? 'person is' : 'people are'} in this preset — they stay, but lose this preset chip.`
        : '';
    const ok = window.confirm(`Delete crew preset "${crew.name}"?${suffix}`);
    if (!ok) return;
    deleteMutation.mutate();
  };

  return (
    <div
      className={cn(
        'flex items-center gap-2 border-b border-border px-3 py-2 last:border-b-0',
        !crew.active && 'opacity-60',
      )}
    >
      {editing ? (
        <>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="h-7 w-10 cursor-pointer rounded border border-border"
            disabled={updateMutation.isPending}
          />
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') updateMutation.mutate();
              if (e.key === 'Escape') {
                e.stopPropagation();
                setEditing(false);
              }
            }}
            disabled={updateMutation.isPending}
            autoFocus
            className="h-8 max-w-[16rem]"
          />
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 hover:text-primary"
            onClick={() => updateMutation.mutate()}
            disabled={updateMutation.isPending || !name.trim()}
            title="Save"
          >
            <CheckIcon className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            onClick={() => setEditing(false)}
            disabled={updateMutation.isPending}
            title="Cancel"
          >
            <XIcon className="h-3.5 w-3.5" />
          </Button>
        </>
      ) : (
        <>
          <span
            className="inline-block h-4 w-4 shrink-0 rounded-full"
            style={{ backgroundColor: crew.color }}
          />
          <span className="flex-1 text-sm font-medium text-foreground">{crew.name}</span>
          <span className="text-xs text-taupe">
            {memberCount} member{memberCount === 1 ? '' : 's'}
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0"
            onClick={() => setEditing(true)}
            title="Rename / change color"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <ActiveToggle
            active={crew.active}
            disabled={toggleActiveMutation.isPending}
            onToggle={() => toggleActiveMutation.mutate(!crew.active)}
            title={crew.active ? 'Preset is active' : 'Preset is inactive'}
          />
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="h-7 w-7 p-0 hover:text-destructive"
            title="Delete preset"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </>
      )}
    </div>
  );
}

// ============================================================
// ADD PERSON — register a field-crew Telegram user (no preset prefill;
// presets are added via the chip strip after creation).
// ============================================================

function AddPersonDialog({
  open,
  onOpenChange,
  activeCrews,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeCrews: CrewRow[];
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [telegramUserId, setTelegramUserId] = useState('');
  const [role, setRole] = useState<CrewMemberRole>('crew');
  const [crewId, setCrewId] = useState('');

  useEffect(() => {
    if (!open) {
      setName('');
      setTelegramUserId('');
      setRole('crew');
      setCrewId('');
    }
  }, [open]);

  const createMutation = useMutation({
    mutationFn: () =>
      postCrewMember({
        telegramUserId: Number(telegramUserId),
        name: name.trim(),
        role,
        crewIds: crewId ? [crewId] : [],
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['crew-members'] });
      onOpenChange(false);
    },
  });

  const trimmedName = name.trim();
  const tgId = Number(telegramUserId);
  const tgValid =
    telegramUserId.length > 0 &&
    Number.isFinite(tgId) &&
    Number.isInteger(tgId) &&
    tgId > 0;
  const canSave = trimmedName.length > 0 && tgValid;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add person</DialogTitle>
          <DialogDescription>
            Register a Telegram bot user. The Telegram ID is the numeric user
            ID — they can find it via @userinfobot or the bot will log it on
            first message. You can add more crew presets after creating them.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label htmlFor="person-name-new" className="eyebrow mb-1.5 block">
              Name *
            </label>
            <Input
              id="person-name-new"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Mike Patel"
              autoFocus
              disabled={createMutation.isPending}
            />
          </div>
          <div>
            <label htmlFor="person-tg-new" className="eyebrow mb-1.5 block">
              Telegram user ID *
            </label>
            <Input
              id="person-tg-new"
              inputMode="numeric"
              value={telegramUserId}
              onChange={(e) => setTelegramUserId(e.target.value.replace(/\D/g, ''))}
              placeholder="e.g. 123456789"
              disabled={createMutation.isPending}
            />
          </div>
          <div>
            <label htmlFor="person-role-new" className="eyebrow mb-1.5 block">
              Role
            </label>
            <select
              id="person-role-new"
              value={role}
              onChange={(e) => setRole(e.target.value as CrewMemberRole)}
              disabled={createMutation.isPending}
              className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {FIELD_ROLE_OPTIONS.filter((r) => r.value !== 'admin').map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="person-crew-new" className="eyebrow mb-1.5 block">
              Crew preset (optional)
            </label>
            <select
              id="person-crew-new"
              value={crewId}
              onChange={(e) => setCrewId(e.target.value)}
              disabled={createMutation.isPending || activeCrews.length === 0}
              className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">
                {activeCrews.length === 0 ? 'No active presets' : 'None'}
              </option>
              {activeCrews.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          {createMutation.error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {createMutation.error instanceof Error
                ? createMutation.error.message
                : 'Failed'}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={!canSave || createMutation.isPending}
          >
            {createMutation.isPending ? 'Adding…' : 'Add person'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// ADD BOT ADMIN — creates role='admin' (no presets). Telegram identity
// with tenant-wide bot visibility; not assignable to jobs.
// ============================================================

function AddBotAdminDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [telegramUserId, setTelegramUserId] = useState('');

  useEffect(() => {
    if (!open) {
      setName('');
      setTelegramUserId('');
    }
  }, [open]);

  const createMutation = useMutation({
    mutationFn: () =>
      postCrewMember({
        telegramUserId: Number(telegramUserId),
        name: name.trim(),
        role: 'admin',
        crewIds: [],
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['crew-members'] });
      onOpenChange(false);
    },
  });

  const trimmedName = name.trim();
  const tgId = Number(telegramUserId);
  const tgValid =
    telegramUserId.length > 0 &&
    Number.isFinite(tgId) &&
    Number.isInteger(tgId) &&
    tgId > 0;
  const canSave = trimmedName.length > 0 && tgValid;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add bot admin</DialogTitle>
          <DialogDescription>
            Bot admins see every crew&apos;s jobs on /schedule and can confirm
            pulls / installs on behalf of any crew. They are not assigned to
            jobs. Their Telegram ID is the numeric user ID — they can find it
            via @userinfobot.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label htmlFor="botadmin-name-new" className="eyebrow mb-1.5 block">
              Name *
            </label>
            <Input
              id="botadmin-name-new"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Office Manager"
              autoFocus
              disabled={createMutation.isPending}
            />
          </div>
          <div>
            <label htmlFor="botadmin-tg-new" className="eyebrow mb-1.5 block">
              Telegram user ID *
            </label>
            <Input
              id="botadmin-tg-new"
              inputMode="numeric"
              value={telegramUserId}
              onChange={(e) => setTelegramUserId(e.target.value.replace(/\D/g, ''))}
              placeholder="e.g. 123456789"
              disabled={createMutation.isPending}
            />
          </div>
          {createMutation.error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {createMutation.error instanceof Error
                ? createMutation.error.message
                : 'Failed'}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={!canSave || createMutation.isPending}
          >
            {createMutation.isPending ? 'Adding…' : 'Add bot admin'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// ADD CREW PRESET
// ============================================================

function AddPresetDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3b82f6');

  useEffect(() => {
    if (!open) {
      setName('');
      setColor('#3b82f6');
    }
  }, [open]);

  const createMutation = useMutation({
    mutationFn: (input: CrewCreateInput) => postCrew(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['crews'] });
      onOpenChange(false);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New crew preset</DialogTitle>
          <DialogDescription>
            A preset is a named, colored group of people you can add to a job in
            one click. Add people to it from the People list above.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label htmlFor="preset-name-new" className="eyebrow mb-1.5 block">
              Name *
            </label>
            <Input
              id="preset-name-new"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. GutterA"
              autoFocus
              disabled={createMutation.isPending}
            />
          </div>
          <div>
            <label htmlFor="preset-color-new" className="eyebrow mb-1.5 block">
              Color
            </label>
            <input
              id="preset-color-new"
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-10 w-20 cursor-pointer rounded border border-border"
              disabled={createMutation.isPending}
            />
          </div>
          {createMutation.error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {createMutation.error instanceof Error
                ? createMutation.error.message
                : 'Failed'}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={createMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => createMutation.mutate({ name: name.trim(), color })}
            disabled={createMutation.isPending || !name.trim()}
          >
            {createMutation.isPending ? 'Creating…' : 'Create preset'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
